/**
 * User Management Page
 *
 * Main page for managing users with search, pagination, and detailed user views.
 * Provides access to user database information, Olo accounts, and Punchh accounts.
 */

import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PaginationControls } from '@/components/pagination-controls';
import { UserManagementTable } from '@/components/user-management/user-management-table';
import { UserDetailModal } from '@/components/user-management/user-detail-modal';
import { useUsers } from '@/hooks/use-user-management';
import type { User } from '@/types/user-management';

/**
 * User Management Page Component
 */
export function Component() {
  // Search and pagination state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [debouncedAppVersion, setDebouncedAppVersion] = useState('');
  const [appBuild, setAppBuild] = useState('');
  const [debouncedAppBuild, setDebouncedAppBuild] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Selected user for detail modal
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Debounce text-filter inputs (300ms) — search + version + build all reset to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setDebouncedAppVersion(appVersion.trim());
      setDebouncedAppBuild(appBuild.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, appVersion, appBuild]);

  // Fetch users with current filters
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useUsers({
    page,
    limit,
    search: debouncedSearch || undefined,
    appVersion: debouncedAppVersion || undefined,
    appBuild: debouncedAppBuild || undefined,
  });

  // Extract data
  const users = usersData?.data || [];
  const pagination = usersData?.pagination;

  /**
   * Handle User Click
   * Opens detail modal for the selected user
   */
  const handleUserClick = (user: User) => {
    setSelectedUserId(user.id);
  };

  /**
   * Handle Close Modal
   */
  const handleCloseModal = () => {
    setSelectedUserId(null);
  };

  /**
   * Handle Page Change
   */
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handle Limit Change
   */
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to page 1 when limit changes
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
        <p className="text-muted-foreground mt-2">
          Manage users, view account details, and manage Olo and Punchh integrations
        </p>
      </div>

      {/* Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardDescription>Total Users</CardDescription>
          <CardTitle className="text-4xl">{pagination?.total || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {debouncedSearch
              ? `Showing results for "${debouncedSearch}"`
              : 'All users in the system'}
          </p>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            Click on a user to view detailed information and manage their accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search + filters */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr,200px,160px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Input
              type="text"
              placeholder="App version (e.g. 2.4.4)"
              value={appVersion}
              onChange={e => setAppVersion(e.target.value)}
            />
            <Input
              type="text"
              placeholder="App build (e.g. 477)"
              value={appBuild}
              onChange={e => setAppBuild(e.target.value)}
            />
          </div>

          {/* Users Table */}
          <UserManagementTable
            users={users}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            onUserClick={handleUserClick}
          />

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal userId={selectedUserId} onClose={handleCloseModal} />
    </div>
  );
}

// Set display name for React Router
Component.displayName = 'UserManagementPage';
